import { debounce } from 'lodash-es'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef, useTransition } from 'react'
import { shallow } from 'zustand/shallow'

import {
  defaultFilters,
  useSearchFilterStore,
  ZSearchFilter,
} from '@/entities/map'
import { RoomSortOption } from '@/entities/room'
import { convertToFilterSearchParams } from '@/features/search-map/lib/search-params'
import { useRouter } from '@/shared/i18n'

const PATHNAME = '/guest/room'

type Props = {
  scrollToTop?: () => void
}

export const useKeywordSearchParams = ({ scrollToTop }: Props = {}) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPageReloading, startTransition] = useTransition()

  const filterActions = useSearchFilterStore(state => state.actions)

  const scrollToTopRef = useRef(scrollToTop)

  const updateSearchParams = () => {
    const roomFilters = useSearchFilterStore.getState().filters
    const { keyword, sort } = useSearchFilterStore.getState()

    const filterSearchParams = convertToFilterSearchParams({
      keyword,
      sort,
      ...roomFilters,
    })

    const scrollToTop = scrollToTopRef.current

    if (scrollToTop) {
      scrollToTop()
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }

    startTransition(() => {
      router.replace(
        `${PATHNAME}${filterSearchParams && `?${filterSearchParams}`}`,
      )
    })
  }

  useEffect(() => {
    const keyword = searchParams?.get('keyword') || ''
    const sort =
      (searchParams?.get('sort') as RoomSortOption) || RoomSortOption.POPULAR

    filterActions.update({
      keyword,
      filters: ZSearchFilter.parse({
        ...defaultFilters,
        ...Object.fromEntries(searchParams?.entries() ?? []),
      }),
      sort,
    })

    const debouncedUpdate = debounce(updateSearchParams, 300)

    const unsubscribe = useSearchFilterStore.subscribe(
      state => ({
        keyword: state.keyword,
        filters: state.filters,
        sort: state.sort,
      }),
      (curr, prev) => {
        if (curr.filters !== prev.filters) {
          debouncedUpdate()
        } else {
          debouncedUpdate.cancel()
          updateSearchParams()
        }
      },
      { equalityFn: shallow },
    )

    return () => {
      unsubscribe()
      debouncedUpdate.cancel()
    }
  }, [])

  return { isPageReloading }
}
